/**
 * CUADRE DE CAJA en la consola (13.3; Mario, 24-oct: «Compacto, por corte al abrir»; CUA-07/08/10 del núcleo).
 * Lógica pura, sin Angular: los cuatro renglones del día, la fila de cada corte, los lotes en vivo y la casilla
 * al cerrar. Pruebas: test/liquidacion-cuadre.test.mjs. Sin imports a propósito: la prueba carga este archivo suelto.
 */

export const textoSinCuadre = 'Sin cuadre (antes de la 13.3)';
export const TEXTO_ASUMO_CUADRE = 'Asumo la responsabilidad de cerrar con el cuadre sin cero';

export interface RenglonCuadre { id: string; titulo: string; ok: boolean | null; monto: number | null; diferencia: number; detalle: string; }

const r2 = (n: number): number => Math.round(n * 100) / 100;
const num = (n: any): number => Number(n) || 0;
const bloque = (bs: any[] | null | undefined, id: string): any => (bs || []).find((b: any) => b && b.id === id);

/** Une varios bloques en un renglón: ok si todos cuadran, la diferencia es lo que no cuadra de cada uno (en positivo), el monto es el del primero que haya. */
const unir = (id: string, titulo: string, bs: any[], montoDe: string, campo: string = ''): RenglonCuadre => {
  const hay = bs.filter(Boolean);
  if (!hay.length) return { id, titulo, ok: null, monto: null, diferencia: 0, detalle: '' };
  const mal = hay.filter((b) => b.ok === false);
  const principal = hay.find((b) => b.id === montoDe) || hay[0];
  return { id, titulo, ok: !mal.length, monto: num(campo && principal[campo] != null ? principal[campo] : principal.esperado), diferencia: r2(mal.reduce((s, b) => s + Math.abs(num(b.diferencia)), 0)), detalle: '' };
};

/** Lo vencido de todo el día: la suma de VENCIDO_TIENE_DESTINO de cada corte que lo trae. */
export const vencidoDeCortes = (cortes: any[] | null | undefined): { esperado: number; medido: number; diferencia: number; ok: boolean; hay: boolean } => {
  const bs = (cortes || []).map((c: any) => bloque(c && c.bloques, 'VENCIDO_TIENE_DESTINO')).filter(Boolean);
  return {
    esperado: r2(bs.reduce((s, b) => s + num(b.esperado), 0)), medido: r2(bs.reduce((s, b) => s + num(b.medido), 0)),
    diferencia: r2(bs.reduce((s, b) => s + (b.ok === false ? Math.abs(num(b.diferencia)) : 0), 0)),
    ok: bs.every((b) => b.ok !== false), hay: bs.length > 0,
  };
};

/** «La cadena de cortes se rompe en el corte 2» (o «en los cortes 2 y 3»); vacío si está bien. */
export const textoCadena = (cadena: any): string => {
  const ns = [...new Set(((cadena && cadena.errores) || []).map((e: any) => Number(e.n)).filter((n: number) => n > 0))].sort((a: any, b: any) => a - b) as number[];
  if (cadena && cadena.ok === false && !ns.length) return 'La cadena de cortes no coincide';
  if (!ns.length) return '';
  return ns.length === 1 ? `La cadena de cortes se rompe en el corte ${ns[0]}` : `La cadena de cortes se rompe en los cortes ${ns.slice(0, -1).join(', ')} y ${ns[ns.length - 1]}`;
};

/** Los cuatro renglones de la tarjeta, con nombre claro; `ok: null` = no hay dato (ni verde ni rojo). */
export const renglonesDelCuadre = (r: any): RenglonCuadre[] => {
  if (!r) return [];
  const b = (id: string) => bloque(r.bloques, id);
  const cobrado = unir('COBRADO', 'Lo cobrado tiene destino', [b('CAJA_DEL_DIA'), b('CONTINUIDAD_ENTRE_CORTES'), b('CONTINUIDAD_ENTRE_DIAS')], 'CAJA_DEL_DIA', 'cobrado');
  const v = b('VENCIDO_TIENE_DESTINO');
  const vc = vencidoDeCortes(r.cortes);
  const vencido: RenglonCuadre = v ? unir('VENCIDO', 'Lo vencido tiene destino', [v], 'VENCIDO_TIENE_DESTINO')
    : { id: 'VENCIDO', titulo: 'Lo vencido tiene destino', ok: vc.hay ? vc.ok : null, monto: vc.hay ? vc.esperado : null, diferencia: vc.diferencia, detalle: '' };
  const liquidado = unir('LIQUIDADO', 'El dinero liquidado tiene destino', [b('DINERO_TIENE_DESTINO'), b('APROBADO_EN_LOTES')], 'DINERO_TIENE_DESTINO');
  const nada = unir('NADA_DOS_VECES', 'Nada se pagó dos veces ni se tocó', [b('NINGUN_ABONO_DOS_VECES')], 'NINGUN_ABONO_DOS_VECES');
  const rota = textoCadena(r.cadena);
  if (r.cadena && r.cadena.ok === false) { nada.ok = false; nada.detalle = rota; if (nada.monto === null) nada.monto = 0; }
  return [cobrado, vencido, liquidado, nada];
};

export interface FilaCorte {
  n: number; hora: string; quien: string; espera_anterior: number; cobrado: number; desconciliado: number; vencido: number; sigue_esperando: number;
  espera: number; diferencia: number; huella: string; ok: boolean | null; sinCuadre: boolean; texto: string;
}

/** HH:MM en hora de Venezuela (UTC-4), igual que liquidacion-cortes. */
const hora = (d: any): string => (d ? new Date(new Date(d).getTime() - 4 * 3600e3).toISOString().slice(11, 16) : '—');

/** La fila de un corte en la tabla «por corte». Un corte sin bloques ni error es de antes del cuadre: texto neutro. */
export const filaDeCorte = (c: any): FilaCorte => {
  const caja = bloque(c && c.bloques, 'CAJA') || {}, venc = bloque(c && c.bloques, 'VENCIDO_TIENE_DESTINO') || {};
  const sinBloques = !c || !c.bloques;
  const conError = !!(c && c.error);
  const sinCuadre = sinBloques && !conError;
  return {
    n: c.n, hora: hora(c.hasta), quien: c.usuario_nombre || c.usuario || '—',
    espera_anterior: num(caja.espera_anterior), cobrado: num(caja.cobrado), desconciliado: num(caja.desconciliado), vencido: num(caja.vencido),
    sigue_esperando: num(venc.sigue_esperando), espera: num(caja.medido), diferencia: num(c.diferencia), huella: c.huella_corta || '',
    ok: sinCuadre ? null : conError ? false : c.ok === false ? false : true, sinCuadre, texto: sinCuadre ? textoSinCuadre : conError ? String(c.error) : '',
  };
};

/** Una fila por corte, en orden de n. */
export const filasDeCortes = (cortes: any[] | null | undefined): FilaCorte[] =>
  [...(cortes || [])].sort((a: any, b: any) => a.n - b.n).map(filaDeCorte);

/** Los lotes en vivo: información neutra, no bloquea nada. */
export const lotesEnVivo = (l: any): { clave: string; texto: string; valor: number }[] => {
  if (!l) return [];
  return [['pagado', 'Pagado'], ['en_banco', 'En el banco'], ['no_procesado', 'No procesado'], ['en_reintento', 'En reintento']].map(([clave, texto]) => ({ clave, texto, valor: num(l[clave]) }));
};

/** Cambia cuando un corte nace o cambia de estado (no con el avance): sirve para refrescar el cuadre solo cuando hace falta. */
export const firmaDeCortes = (cortes: any[] | null | undefined): string => (cortes || []).map((c: any) => `${c.n}:${c.estado}`).join('|');

/** Cerrar el día exige la casilla del cuadre solo si la vista previa pide responsable del cuadre. */
export const puedeCerrarConCuadre = (cuadre: any, asumoCuadre: boolean): boolean => !(cuadre && cuadre.requiere_responsable) || !!asumoCuadre;
